import React, { useEffect, useState } from 'react';
import AppShell from '@/components/layout/AppShell';
import { financeNav } from '@/lib/navConfig';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Wallet, TrendingUp, TrendingDown, ArrowUpRight, ArrowDownRight, Receipt, PiggyBank } from 'lucide-react';
import api, { fmtIDR, fmtDate } from '@/lib/api';
import { catMeta } from '@/lib/expenseCategories';
import { toast } from 'sonner';

export default function FinanceDashboard() {
  const [balance, setBalance] = useState(null);
  const [recent, setRecent] = useState([]);

  const load = async () => {
    try {
      const [b, m] = await Promise.all([
        api.get('/cash/balance'),
        api.get('/cash/movements?limit=15'),
      ]);
      setBalance(b.data);
      setRecent(m.data);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Gagal memuat'); }
  };
  useEffect(() => { load(); }, []);

  return (
    <AppShell nav={financeNav} title="Dashboard Finance">
      <div className="space-y-6">
        {/* Balance hero */}
        <Card className="bg-gradient-to-br from-primary/10 via-background to-background border-primary/30">
          <CardContent className="p-6 sm:p-8">
            <div className="flex items-center gap-3 mb-2">
              <div className="h-12 w-12 rounded-xl bg-primary/15 text-primary flex items-center justify-center">
                <PiggyBank className="h-6 w-6" />
              </div>
              <div>
                <div className="text-xs text-muted-foreground">Saldo Kas Perusahaan</div>
                <div className="text-3xl sm:text-4xl font-heading font-semibold tabular-nums" data-testid="finance-balance">
                  {fmtIDR(balance?.balance || 0)}
                </div>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3 mt-4">
              <div className="rounded-lg border bg-background p-3">
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <ArrowUpRight className="h-3.5 w-3.5 text-emerald-600" /> Total Kas Masuk
                </div>
                <div className="text-lg font-semibold tabular-nums text-emerald-700">{fmtIDR(balance?.total_topups || 0)}</div>
                <div className="text-[10px] text-muted-foreground">{balance?.count_topups || 0} kali topup</div>
              </div>
              <div className="rounded-lg border bg-background p-3">
                <div className="flex items-center gap-2 text-xs text-muted-foreground">
                  <ArrowDownRight className="h-3.5 w-3.5 text-rose-600" /> Total Pengeluaran
                </div>
                <div className="text-lg font-semibold tabular-nums text-rose-700">{fmtIDR(balance?.total_expenses || 0)}</div>
                <div className="text-[10px] text-muted-foreground">{balance?.count_expenses || 0} transaksi</div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Recent activity */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-base flex items-center gap-2"><Receipt className="h-4 w-4" />Riwayat Terbaru</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            {recent.length === 0 ? (
              <div className="text-center py-8 text-sm text-muted-foreground">Belum ada aktivitas kas.</div>
            ) : (
              <div className="overflow-x-auto">
                <Table data-testid="finance-recent-table">
                  <TableHeader>
                    <TableRow>
                      <TableHead>Waktu</TableHead>
                      <TableHead>Tipe</TableHead>
                      <TableHead>Kategori / Penerima</TableHead>
                      <TableHead>Petugas</TableHead>
                      <TableHead className="text-right">Nominal</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {recent.map((m) => {
                      const cm = m.category ? catMeta(m.category) : null;
                      return (
                        <TableRow key={m.id} data-testid={`finance-mov-${m.id}`}>
                          <TableCell className="text-xs text-muted-foreground whitespace-nowrap">{fmtDate(m.created_at)}</TableCell>
                          <TableCell>
                            {m.type === 'in' ? (
                              <Badge className="bg-emerald-100 text-emerald-700 hover:bg-emerald-100 border-0"><TrendingUp className="h-3 w-3 mr-1" />Kas Masuk</Badge>
                            ) : (
                              <Badge className="bg-rose-100 text-rose-700 hover:bg-rose-100 border-0"><TrendingDown className="h-3 w-3 mr-1" />Pengeluaran</Badge>
                            )}
                          </TableCell>
                          <TableCell>
                            {m.type === 'out' && cm ? (
                              <div className="text-sm">
                                <div className="font-medium">{cm.label}</div>
                                <div className="text-[10px] text-muted-foreground">Untuk: {m.recipient_name || '-'}</div>
                              </div>
                            ) : (
                              <div className="text-sm text-muted-foreground italic">{m.description || 'Topup kas'}</div>
                            )}
                          </TableCell>
                          <TableCell className="text-xs">{m.created_by_name || '-'}</TableCell>
                          <TableCell className={`text-right tabular-nums font-semibold ${m.type === 'in' ? 'text-emerald-700' : 'text-rose-700'}`}>
                            {m.type === 'in' ? '+' : '-'} {fmtIDR(m.amount)}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AppShell>
  );
}
